import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "../atoms/Button/index.js";
import { Tooltip } from "../molecules/Tooltip/index.js";

/** Task 6 — the viewer's mention count and navigation, as the host reports it. */
export interface PlayerAIMentionNavigation {
  /**
   * Messages in the WHOLE conversation that mention the viewer, each counted
   * once. A total, not an unread count: reading one changes nothing.
   */
  readonly count: number;
  /**
   * Go to the previous (older) or next (newer) mention. `from` is where the
   * reader is: the sequences of the first and last rows on screen, so the
   * host can pick the neighbouring mention and load older history for it.
   */
  readonly onNavigate: (
    direction: "previous" | "next",
    from: { readonly first: number | null; readonly last: number | null },
  ) => void;
  /** A navigation is loading older history. */
  readonly busy?: boolean | undefined;
}

/**
 * "N mentions ↑ ↓" at the START of the existing header row, so the current
 * icons keep their place at its end and no second row appears. Hidden while
 * the viewer has no mentions at all.
 */
export function MentionNavigator({
  navigation,
  visibleRange,
}: {
  navigation: PlayerAIMentionNavigation;
  visibleRange: () => { first: number | null; last: number | null };
}) {
  if (navigation.count <= 0) return null;
  const label =
    navigation.count === 1 ? "1 mention" : `${navigation.count} mentions`;
  return (
    <div
      role="group"
      aria-label="Your mentions"
      className="flex min-w-0 items-center gap-0.5"
      data-mention-navigator
    >
      <span className="me-0.5 truncate text-dense-1 text-pb-text-secondary">
        {label}
      </span>
      <Tooltip content="Previous mention" describeChild={false}>
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          aria-label="Previous mention"
          disabled={navigation.busy === true}
          onClick={() => navigation.onNavigate("previous", visibleRange())}
        >
          <ArrowUp className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </Tooltip>
      <Tooltip content="Next mention" describeChild={false}>
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          aria-label="Next mention"
          disabled={navigation.busy === true}
          onClick={() => navigation.onNavigate("next", visibleRange())}
        >
          <ArrowDown className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </Tooltip>
    </div>
  );
}
